import { format } from 'date-fns/format';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Container } from 'react-bootstrap';
import styled from 'styled-components';
import GenericButton from '../../components/GenericButton';
import useModalStrip from '../../hooks/global-hooks/useModalStrip';
import { getAllActiveGameConfigs } from '../../redux/entities/gameEntity';
import { getSingleReport, saveSingleReport } from '../../redux/entities/transactionEntity';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { downloadPdf, SinglePdf } from './calculatorPdf';
import {
    applyPaint, ButtonContainer, Cell, CellColors, ColorBar, DAYS, DayKey, DayPanel, DIGITS, emptyExtras,
    ExtraBoxes, HeaderCell, Layout, PageHeading, Paint, parseColors, parseExtras, parsePoints, Sheet,
    SheetContainer, SheetExtras, SheetScroll, TotalCell,
    useDayDates, useNoZoomViewport, useUnsavedWarning,
} from './calculatorShared';

// Pinned to the left so game names stay visible while scrolling the digits
const NameCell = styled(HeaderCell)`
  text-align: left;
  padding: 4px 8px;
  position: sticky;
  left: 0;
  z-index: 1;

  @media (max-width: 576px) {
    width: 64px;
    padding: 4px 3px;
    font-size: 10px;
    white-space: nowrap;
    overflow-wrap: normal;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

// Right-hand copy of the name, as in the paper sheet
const RepeatNameCell = styled(HeaderCell)`
  text-align: left;
  padding: 4px 8px;

  @media (max-width: 576px) {
    width: 64px;
    padding: 4px 3px;
    font-size: 10px;
    white-space: nowrap;
    overflow-wrap: normal;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

// With names on both sides the phone grid is too narrow to fit, so it scrolls
// inside its box (the left name stays pinned)
const SingleSheet = styled(Sheet)`
  @media (max-width: 576px) {
    min-width: 520px;
  }
`;

const PaintableCell = styled(Cell)<{ $painting: boolean }>`
  cursor: ${({ $painting }) => ($painting ? 'pointer' : 'auto')};
  input {
    cursor: ${({ $painting }) => ($painting ? 'pointer' : 'text')};
  }
`;

const TotalHeaderCell = styled(HeaderCell)`
  @media (max-width: 576px) {
    width: 48px;
  }
`;

interface ClientRow {
    clientName: string;
    numbers: Array<{ value: string; points: string }>;
    total: number;
}

interface SheetData {
    clients: ClientRow[];
    total: number;
    colors: CellColors;
    extras: SheetExtras;
}

const cellKey = (clientName: string, digit: string) => `${clientName}|${digit}`;

const emptyRow = (clientName: string): ClientRow => ({
    clientName,
    numbers: DIGITS.map(value => ({ value, points: '' })),
    total: 0,
});

const rowTotal = (row: ClientRow) =>
    row.numbers.reduce((sum, n) => sum + parsePoints(n.points), 0);

const pointsOf = (row: ClientRow, digit: string) =>
    parsePoints(row.numbers.find(n => n.value === digit)?.points);

// One row per game name; saved rows are matched by name so new games still appear
const buildSheet = (gameNames: string[], saved?: SheetData | null): SheetData => {
    const savedByName = new Map((saved?.clients || []).map(c => [c.clientName, c]));
    const names = [...gameNames, ...Array.from(savedByName.keys()).filter(n => !gameNames.includes(n))];
    const clients = names.map(name => {
        const savedRow = savedByName.get(name);
        const row = emptyRow(name);
        if (savedRow) {
            row.numbers = DIGITS.map(value => ({
                value,
                points: savedRow.numbers?.find(n => n.value === value)?.points || '',
            }));
            row.total = rowTotal(row);
        }
        return row;
    });
    return {
        clients,
        total: clients.reduce((sum, c) => sum + c.total, 0),
        colors: parseColors(saved?.colors),
        extras: saved?.extras ? parseExtras(saved.extras) : emptyExtras(),
    };
};

const SingleReportPage = () => {
    const dispatch = useAppDispatch();
    const { showModalStrip } = useModalStrip();
    const gameConfigs = useAppSelector((state) => state.entities.gameReducer.activeGameConfigs);
    const [isLoading, setIsLoading] = useState(false);
    const [hasFetched, setHasFetched] = useState(false);
    const [selectedDay, setSelectedDay] = useState<DayKey>('AAJ');
    const [sheets, setSheets] = useState<Partial<Record<DayKey, SheetData>>>({});
    const [dirtyDays, setDirtyDays] = useState<DayKey[]>([]);
    const [isSaving, setIsSaving] = useState(false);
    const [paint, setPaint] = useState<Paint>(null);
    // Read inside async loads, so a late response never overwrites what the user typed
    const dirtyRef = useRef<DayKey[]>([]);
    dirtyRef.current = dirtyDays;

    useNoZoomViewport();
    useUnsavedWarning(dirtyDays.length > 0);
    const dayDates = useDayDates();

    useEffect(() => {
        const fetchGameConfigs = async () => {
            if ((!gameConfigs || gameConfigs.length === 0) && !isLoading && !hasFetched) {
                try {
                    setIsLoading(true);
                    await dispatch(getAllActiveGameConfigs()).unwrap();
                    setHasFetched(true);
                } catch (err) {
                    showModalStrip('danger', 'Failed to load game configurations', 5000);
                } finally {
                    setIsLoading(false);
                }
            }
        };

        fetchGameConfigs();
    }, [dispatch, gameConfigs, isLoading, hasFetched, showModalStrip]);

    const gameNames = useMemo(
        () => Array.from(new Set(gameConfigs.map(config => config?.game?.name).filter(Boolean))) as string[],
        [gameConfigs]
    );

    // The report API is keyed by game + result time + date, but this sheet covers all
    // games for a date, so the whole sheet is stored under one fixed anchor config.
    const anchorConfig = useMemo(
        () => [...gameConfigs].sort((a, b) => (a?.id ?? 0) - (b?.id ?? 0))[0],
        [gameConfigs]
    );

    // Resolves true if the day loaded; on failure the day starts as an empty sheet
    const loadDay = useCallback(async (day: DayKey): Promise<boolean> => {
        if (!anchorConfig) return true;
        try {
            const response = await dispatch(getSingleReport({
                gameId: anchorConfig.game?.id,
                date: format(dayDates[day], 'yyyy-MM-dd'),
                resultTimeId: Number(anchorConfig.resultTime?.id),
                type: 'Single',
            })).unwrap();
            let saved = response?.reportSheetData ?? null;
            if (typeof saved === 'string') saved = JSON.parse(saved);
            if (!dirtyRef.current.includes(day)) {
                setSheets(prev => ({ ...prev, [day]: buildSheet(gameNames, saved) }));
            }
            return true;
        } catch (error) {
            if (!dirtyRef.current.includes(day)) {
                setSheets(prev => ({ ...prev, [day]: buildSheet(gameNames) }));
            }
            return false;
        }
    }, [anchorConfig, dayDates, dispatch, gameNames]);

    useEffect(() => {
        Promise.all(DAYS.map(d => loadDay(d.key))).then(results => {
            const failed = DAYS.filter((_, i) => !results[i]).map(d => d.key);
            if (failed.length) {
                showModalStrip('danger', `Could not load saved data for ${failed.join(', ')}`, 5000);
            }
        });
    }, [loadDay, showModalStrip]);

    const sheetData = sheets[selectedDay] || buildSheet(gameNames);

    const updateSheet = (change: (current: SheetData) => SheetData) => {
        setDirtyDays(prev => (prev.includes(selectedDay) ? prev : [...prev, selectedDay]));
        setSheets(prev => ({ ...prev, [selectedDay]: change(prev[selectedDay] || buildSheet(gameNames)) }));
    };

    const handlePointChange = (clientIndex: number, digit: string, value: string) => {
        const numericValue = value.replace(/[^0-9]/g, '');
        updateSheet(current => {
            const clients = current.clients.map((row, i) => {
                if (i !== clientIndex) return row;
                const updated = {
                    ...row,
                    numbers: row.numbers.map(n => (n.value === digit ? { ...n, points: numericValue } : n)),
                };
                return { ...updated, total: rowTotal(updated) };
            });
            return { ...current, clients, total: clients.reduce((sum, c) => sum + c.total, 0) };
        });
    };

    const handlePaint = (clientName: string, digit: string) => {
        if (paint === null) return;
        updateSheet(current => ({ ...current, colors: applyPaint(current.colors, cellKey(clientName, digit), paint) }));
    };

    const handleExtraChange = (field: keyof SheetExtras, value: string) => {
        updateSheet(current => ({ ...current, extras: { ...current.extras, [field]: value } }));
    };

    const digitTotal = (d: string) => sheetData.clients.reduce((sum, c) => sum + pointsOf(c, d), 0);

    const handleDownload = () => {
        const date = dayDates[selectedDay];
        const pointsByName = new Map(sheetData.clients.map(c => [c.clientName, c]));
        downloadPdf(
            <SinglePdf
                subtitle={`${selectedDay} - ${format(date, 'dd MMM yyyy')}`}
                clients={sheetData.clients}
                pointsOf={(name, d) => pointsByName.get(name)?.numbers.find(n => n.value === d)?.points || ''}
                digitTotals={Object.fromEntries(DIGITS.map(d => [d, digitTotal(d)]))}
                total={sheetData.total}
                colors={sheetData.colors}
                cellKey={cellKey}
                extras={sheetData.extras}
            />,
            `single-${selectedDay}-${format(date, 'yyyy-MM-dd')}.pdf`
        ).catch(() => showModalStrip('danger', 'Failed to create PDF', 5000));
    };

    const handleSubmit = () => {
        if (isSaving) return;
        if (!anchorConfig) {
            showModalStrip('warning', 'No active games found', 2000);
            return;
        }

        const day = selectedDay;
        setIsSaving(true);
        dispatch(saveSingleReport({
            gameId: anchorConfig.game?.id,
            date: format(dayDates[selectedDay], 'yyyy-MM-dd'),
            resultTimeId: Number(anchorConfig.resultTime?.id),
            type: 'Single',
            reportSheetData: sheetData,
        }))
            .unwrap()
            .then(() => {
                setDirtyDays(prev => prev.filter(d => d !== day));
                showModalStrip('success', `${day} report saved successfully`, 2000);
            })
            .catch((err: unknown) => {
                showModalStrip('danger', typeof err === 'string' ? err : 'Failed to save report', 5000);
            })
            .finally(() => setIsSaving(false));
    };

    return (
        <Container fluid>
            <PageHeading>Single Calculator</PageHeading>

            <Layout>
                <SheetContainer>
                    <ColorBar paint={paint} onChange={setPaint} />
                    <SheetScroll>
                    <SingleSheet>
                        <thead>
                            <tr>
                                <NameCell as="th">NAME</NameCell>
                                {DIGITS.map(d => (
                                    <HeaderCell as="th" key={d}>{d}</HeaderCell>
                                ))}
                                <RepeatNameCell as="th">NAME</RepeatNameCell>
                                <TotalHeaderCell as="th">TOTAL</TotalHeaderCell>
                            </tr>
                        </thead>
                        <tbody>
                            {sheetData.clients.map((client, clientIndex) => (
                                <tr key={client.clientName}>
                                    <NameCell>{client.clientName}</NameCell>
                                    {DIGITS.map(d => (
                                        <PaintableCell
                                            key={d}
                                            $painting={paint !== null}
                                            style={{ background: sheetData.colors[cellKey(client.clientName, d)] }}
                                            onClick={() => handlePaint(client.clientName, d)}
                                        >
                                            <input
                                                type="text"
                                                inputMode="numeric"
                                                pattern="[0-9]*"
                                                autoComplete="off"
                                                readOnly={paint !== null}
                                                value={client.numbers.find(n => n.value === d)?.points || ''}
                                                onChange={(e) => handlePointChange(clientIndex, d, e.target.value)}
                                            />
                                        </PaintableCell>
                                    ))}
                                    <RepeatNameCell>{client.clientName}</RepeatNameCell>
                                    <TotalCell>{client.total}</TotalCell>
                                </tr>
                            ))}
                            <tr>
                                <NameCell>TOTAL</NameCell>
                                {DIGITS.map(d => (
                                    <TotalCell key={d}>{digitTotal(d)}</TotalCell>
                                ))}
                                <RepeatNameCell>TOTAL</RepeatNameCell>
                                <TotalCell>{sheetData.total}</TotalCell>
                            </tr>
                            <tr>
                                <NameCell />
                                {DIGITS.map(d => (
                                    <HeaderCell key={d}>{d}</HeaderCell>
                                ))}
                                <RepeatNameCell />
                                <HeaderCell />
                            </tr>
                        </tbody>
                    </SingleSheet>
                    </SheetScroll>
                    <ExtraBoxes extras={sheetData.extras} onChange={handleExtraChange} />
                    <ButtonContainer>
                        <GenericButton
                            label="Download PDF"
                            type="secondary"
                            onClick={handleDownload}
                        />
                        <GenericButton
                            label={isSaving ? 'Saving...' : `Save ${selectedDay}`}
                            onClick={handleSubmit}
                            disabled={isSaving}
                        />
                    </ButtonContainer>
                </SheetContainer>

                <DayPanel
                    dayDates={dayDates}
                    totals={Object.fromEntries(DAYS.map(d => [d.key, sheets[d.key]?.total ?? 0]))}
                    dirtyDays={dirtyDays}
                    selectedDay={selectedDay}
                    onSelect={setSelectedDay}
                />
            </Layout>
        </Container>
    );
};
export default SingleReportPage;
